import { CommonModule } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Host,
  Input,
  Optional,
  Output,
  SkipSelf,
} from "@angular/core";
import {
  ControlContainer,
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { NgxCurrencyModule } from "ngx-currency";
import { NgxMaskDirective } from "ngx-mask";

@Component({
  selector: "app-input",
  standalone: true,
  template: `
    <div class="flex flex-col">
      <mat-label
        [class.text-danger]="
          control?.hasError('required') && (control.touched || control.dirty)
        "
        *ngIf="label"
      >
        {{ required ? "*" + label : label }}
      </mat-label>
      <mat-form-field [class.fill]="variant === 'fill'" appearance="outline">
        <input
          *ngIf="!isCurrency; else currency"
          (paste)="onPaste($event)"
          [class.!pl-4]="iconPrefix"
          min="0"
          matInput
          [formControl]="control"
          [readonly]="readonly"
          [type]="type"
          [mask]="mask"
          [dropSpecialCharacters]="false"
          [placeholder]="placeholder"
          [prefix]="prefix"
          [thousandSeparator]="thousandSeparator"
          (keydown)="onKeyDown($event)"
          (input)="onValueChange($event)"
          (change)="fullChange.emit($event)"
          [required]="required"
          (blur)="touched()"
          [maxLength]="maxLength"
          [minLength]="minLength"
          autocomplete="new-password"
        />
        <ng-template #currency>
          <div class="flex">
            <span matTextPrefix>$&nbsp;</span>
            <input
              (paste)="onPaste($event)"
              [class.!pl-4]="iconPrefix"
              matInput
              currencyMask
              [formControl]="control"
              [readonly]="readonly"
              [type]="type"
              [placeholder]="placeholder"
              (keyup)="onValueChange($event)"
              (change)="fullChange.emit($event)"
              [required]="required"
              (blur)="touched()"
              [maxLength]="maxLength"
              [minLength]="minLength"
            />
          </div>
        </ng-template>
        <!--        <span matPrefix class="pl-[10px]" *ngIf="iconPrefix"-->
        <!--          ><i [class]="iconPrefix" class="mt-[-20px]"></i-->
        <!--        ></span>-->
        <ng-template
          ngFor
          let-error
          [ngForOf]="errors | keyvalue"
          ngProjectAs="mat-error"
        >
          <mat-error *ngIf="control?.hasError(error.key)">
            {{ error.value }}
          </mat-error>
        </ng-template>
      </mat-form-field>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
    NgxMaskDirective,
    NgxCurrencyModule,
  ],
})
export class InputComponent implements ControlValueAccessor {
  readonly defaultErrors: Record<string, string> = {
    required: "Required",
    minlength: "Invalid Format",
    mask: "Invalid Format",
    email: "Invalid Email Address format",
    pattern: "Invalid Email Address format",
  };
  @Input() required = false;
  @Input() readonly: boolean = false;
  @Input() disabled = false;
  @Input() label = "";
  @Input() placeholder = "";
  @Input() formControlName!: string;
  @Input() type = "text";
  @Input() mask: string = "";
  @Input() prefix: string = "";
  @Input() thousandSeparator: string = ",";
  @Input() maxLength: number = 100000;
  @Input() minLength: number = 0;
  @Input() iconPrefix: string = "";
  @Input() isCurrency = false;
  @Input() onlyPositive = false;
  @Input() isViewOnlyMode = false;
  @Input() possibleErrors: Record<string, string> = {};
  @Input() variant: "outline" | "fill" = "outline";
  @Output() onKeyUp = new EventEmitter<any>();
  @Output() valueChange = new EventEmitter<any>();
  @Output() fullChange = new EventEmitter<any>();

  value: any;
  pristine = true;

  control!: any | null;
  errors!: Record<string, string>;

  constructor(
    @Optional()
    @Host()
    @SkipSelf()
    private controlContainer: ControlContainer,
    private elementRef: ElementRef
  ) {
    this.onChange = () => {};
    this.onTouched = () => {};
  }

  ngOnInit() {
    if (this.controlContainer) {
      if (this.formControlName) {
        this.control = this.controlContainer?.control?.get(
          this.formControlName
        );

        if (this.disabled) {
          this.control.disable();
        }
        this.registerOnChange((value: any) => {
          this.onChange = value;
        });
      }
    }
    this.errors = {
      ...this.defaultErrors,
      ...this.possibleErrors,
    };
  }

  onChange: (value: any) => void;
  onTouched: () => void;

  writeValue(value: any): void {
    this.value = value;
  }

  onPaste(e: any) {
    this.valueChange.emit({
      control: this.formControlName,
      value: e.clipboardData.getData("text/plain"),
    });
  }

  setFocus() {
    this.elementRef.nativeElement.querySelector("input").focus();
  }

  registerOnChange(onChange: (value: any) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }
  onKeyDown(event: KeyboardEvent): void {
    const key = event.key;
    if (
      ["Backspace", "ArrowLeft", "ArrowRight", "Delete", "Tab"].includes(key)
    ) {
      return;
    }
    if (this.onlyPositive && !/^[0-9]$/.test(key)) {
      event.preventDefault();
    }
  }
  onValueChange(e: any): void {
    this.value = e.target.value;
    this.onChange(this.value);
    this.valueChange.emit({
      control: this.formControlName,
      value: e.target.value,
    });
    this.onKeyUp.emit(e);
  }

  touched() {
    this.onTouched();
  }
}
